import { Component, OnDestroy, OnInit, Input, Output, EventEmitter, OnChanges, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { of, from, iif, Subscription, tap } from 'rxjs';
import { map, concatMap, filter, switchMap, finalize, concatAll, catchError } from 'rxjs/operators';

import { MessageService } from 'primeng/api';

import * as XLSX from 'xlsx';

import { environment } from 'src/environments/environment';
import { parseMyFloat, parseTextBuffer, parseMyFloatEx } from 'src/app/shared/round';

import { Product, ProdVendors } from 'src/app/shared/product'
import { ProductGroup } from 'src/app/shared/product-group'

import { Client } from 'src/app/shared/client';
import { ImportItemFields } from 'src/app/shared/document';

import { GlobalSettingsData } from 'src/app/shared/global-settings-data'
import { GetglobalsettingsService } from 'src/app/services/getglobalsettings.service'

import { ProductApiService } from 'src/app/services/product-api.service';
import { ClientApiService } from 'src/app/services/client-api.service';
import { RestApiService } from 'src/app/services/rest-api.service';
import { PriceVariable } from 'src/app/shared/price-variable';

/**
 * Modal dialog for bulk import and update of `Product` records from an Excel file or
 * tab/CSV text.
 *
 * ### Import flow
 * 1. **File selector** — user uploads an `.xlsx` file or pastes text;
 *    `processDataSource` extracts column headers and auto-maps them to `paramsMap`
 *    entries (by `fieldLabel`, `fieldName`, or `anotherFieldNames` aliases).
 * 2. **Field selector** — user reviews column mapping; `canInsertData` guards
 *    the Next button (all mandatory fields must be mapped).
 * 3. **Import result** — `onDataInsert` iterates rows via `from + concatMap`:
 *    - Looks up each PRODCODE with `getProductByCode`.
 *    - If the product exists and `overwriteExisted` is true, updates fields.
 *    - If the product does not exist and `createNewOnly` is false, creates it
 *      using `newProductTemplate` as the base.
 *    - For VENDORINFO fields, posts `ProdVendors` records after the product is saved.
 * 4. **Confirm** — `onImportContent` emits `importIsDone` with the result `Product[]`.
 *
 * ### Two `fieldArea` namespaces
 * - `'PRODUCT'`    — 21 fields mapped directly to `Product` properties.
 * - `'VENDORINFO'` — 14 fields mapped to `ProdVendors` (supplier pricing / margin).
 *
 * `hasWarningsOrErrors(row)` helper returns true when a row's `HasWarningsOrErrors`
 * flag is set, used by the result table to highlight problem rows.
 */
@Component({
  selector: 'app-import-product-info-dlg',
  templateUrl: './import-product-info-dlg.component.html',
  styleUrls: ['./import-product-info-dlg.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class ImportProductInfoDlgComponent implements OnInit, OnChanges, OnDestroy {
  private globalSettinsData: GlobalSettingsData;

  /** Controls dialog visibility; two-way bound via `displayChange`. */
  @Input() display: boolean = false;

  /** Dialog header text. */
  @Input() header: string = 'Импорт/обновление товаров';

  /**
   * Template `Product` used as the base when creating new products during import.
   * Supplies default PRODGROUPID, PRODVENDORID, and other required fields.
   * A warning is shown in the template when this is `null`.
   */
  @Input() newProductTemplate: Product = null;

  noTemplateMessage: string = 'Отсутствует выбраный товар для использования в качестве шаблона при создании новых. Импорт новых товаров может быть невозможен.';
  /*
  * This is only for import field selector definitions
  */
  @Input() paramsMap: ImportItemFields[] = [

    {
      fieldName: 'RESULT',
      fieldLabel: 'Статус',
      fieldIsMandatory: false,
      fieldType: 'str',
      fieldArea: 'PRODUCT',
      updateExisted: false,
    },
    {
      fieldName: 'PRODCODE', fieldLabel: 'Код товара', fieldIsMandatory: true, fieldType: 'str', fieldArea: 'PRODUCT', anotherFieldNames: ['Model Name', 'PART_NO', 'Mouser Part Number'], updateExisted: false,},
    { fieldName: 'PRODARTICUL', fieldLabel: 'Артикул', fieldIsMandatory: false, fieldType: 'str', fieldArea: 'PRODUCT', anotherFieldNames: ['Part No'], updateExisted: false, },
    { fieldName: 'PRODBARCODEMASK', fieldLabel: 'Штрихкод', fieldIsMandatory: false, fieldType: 'str', fieldArea: 'PRODUCT', anotherFieldNames: ['Barcode'], updateExisted: false, },
    { fieldName: 'PRODNAME', fieldLabel: 'Наименование товара', fieldIsMandatory: false, fieldType: 'str', fieldArea: 'PRODUCT', anotherFieldNames: ['Description'], updateExisted: false, },
    { fieldName: 'PRODDESCRIPTION', fieldLabel: 'Описание', fieldIsMandatory: false, fieldType: 'str', fieldArea: 'PRODUCT', updateExisted: false, },
    { fieldName: 'PRODNOTE', fieldLabel: 'Для бухгалтерии', fieldIsMandatory: false, fieldType: 'str', fieldArea: 'PRODUCT', updateExisted: false, },
    {
      fieldName: 'PRODBASEDISC', fieldLabel: 'Скидка 1', fieldIsMandatory: false, fieldType: 'num', fieldArea: 'PRODUCT', anotherFieldNames: ['DISC1', 'Минимальная скидка'], defaultValue: '5', updateExisted: false, },
    {
      fieldName: 'PRODQTYDISC', fieldLabel: 'Скидка 2', fieldIsMandatory: false, fieldType: 'num', fieldArea: 'PRODUCT', anotherFieldNames: ['DISC2', 'Средняя скидка'], defaultValue: '10', updateExisted: false, },
    {
      fieldName: 'PRODDIALDISC', fieldLabel: 'Скидка 3', fieldIsMandatory: false, fieldType: 'num', fieldArea: 'PRODUCT', anotherFieldNames: ['DISC3', 'Максимальная скидка'], defaultValue: '25', updateExisted: false, },

    { fieldName: 'PRODVAT', fieldLabel: 'Ставка НДС', fieldIsMandatory: false, fieldType: 'num', fieldArea: 'PRODUCT', defaultValue: '', updateExisted: false,  }, // defaultValue is set from the configured VAT rate in ngOnInit
    { fieldName: 'PRODCUSTCODE', fieldLabel: 'Таможенный код', fieldIsMandatory: false, fieldType: 'str', fieldArea: 'PRODUCT', updateExisted: false, },
    { fieldName: 'PRODWEIGHT', fieldLabel: 'Вес', fieldIsMandatory: false, fieldType: 'num', fieldArea: 'PRODUCT', updateExisted: false, },
    { fieldName: 'PRODPACK', fieldLabel: 'В упаковке', fieldIsMandatory: false, fieldType: 'num', fieldArea: 'PRODUCT', updateExisted: false, },
    { fieldName: 'PRODPACKGW', fieldLabel: 'Вес упаковки', fieldIsMandatory: false, fieldType: 'num', fieldArea: 'PRODUCT', updateExisted: false, },
    { fieldName: 'PRODPACKVOL', fieldLabel: 'Объем упаковки', fieldIsMandatory: false, fieldType: 'num', fieldArea: 'PRODUCT', updateExisted: false, },
    { fieldName: 'PRODWARRANTY', fieldLabel: 'Гарантия', fieldIsMandatory: false, fieldType: 'num', fieldArea: 'PRODUCT', updateExisted: false, },
    { fieldName: 'PRODDELTERM', fieldLabel: 'Срок поставки', fieldIsMandatory: false, fieldType: 'num', fieldArea: 'PRODUCT', updateExisted: false, },
    { fieldName: 'PRODWWWPAGE', fieldLabel: 'Ссылка в интернете', fieldIsMandatory: false, fieldType: 'str', fieldArea: 'PRODUCT', updateExisted: false, },

    { fieldName: 'PRODGROUPNAME', fieldLabel: 'Группа', fieldIsMandatory: false, fieldType: 'str', fieldArea: 'PRODUCT', anotherFieldNames: ['PGROUPNAME'], updateExisted: false, },
    { fieldName: 'PRODVENDORNAME', fieldLabel: 'Производитель', fieldIsMandatory: false, fieldType: 'str', fieldArea: 'PRODUCT', anotherFieldNames: ['Manufacturer', 'Manufacturer Name', 'OEM'], updateExisted: false, },

    { fieldName: 'VENDORNIKNAME', fieldLabel: 'Поставщик', fieldIsMandatory: false, fieldType: 'str', fieldArea: 'VENDORINFO', updateExisted: false, },
    {
      fieldName: 'VENDORPRICE', fieldLabel: 'Цена поставщика', fieldIsMandatory: false, fieldType: 'num', fieldArea: 'VENDORINFO', anotherFieldNames: ['UNIT_PRICE', 'Distributor Price', 'Price'], updateExisted: true,
},
    { fieldName: 'CURCODE', fieldLabel: 'Валюта поставщика', fieldIsMandatory: false, fieldType: 'str', fieldArea: 'VENDORINFO', updateExisted: true, },
    { fieldName: 'VPADDCONST', fieldLabel: 'Доставка', fieldIsMandatory: false, fieldType: 'num', fieldArea: 'VENDORINFO', anotherFieldNames: ['Delivery'], updateExisted: true, },
    { fieldName: 'VPMULCONST', fieldLabel: 'Пошлина', fieldIsMandatory: false, fieldType: 'num', fieldArea: 'VENDORINFO', updateExisted: true, },
    { fieldName: 'VENDORMARGIN', fieldLabel: 'Маржа', fieldIsMandatory: false, fieldType: 'num', fieldArea: 'VENDORINFO', defaultValue: '1.65', updateExisted: true, },
    { fieldName: 'VENROUNDBASE', fieldLabel: 'Округление', fieldIsMandatory: false, fieldType: 'num', fieldArea: 'VENDORINFO', updateExisted: true, },

    { fieldName: 'VAVARVALUE', fieldLabel: 'Дополнительно +', fieldIsMandatory: false, fieldType: 'num', fieldArea: 'VENDORINFO', anotherFieldNames: ['VARVALUEAD'], updateExisted: false, },
    { fieldName: 'VAVARCODE', fieldLabel: 'Переменная +', fieldIsMandatory: false, fieldType: 'str', fieldArea: 'VENDORINFO', anotherFieldNames: ['VARCODEAD'], updateExisted: false, },
    { fieldName: 'VMVARVALUE', fieldLabel: 'Дополнительно *', fieldIsMandatory: false, fieldType: 'num', fieldArea: 'VENDORINFO', anotherFieldNames: ['VARVALUEML'], updateExisted: false, },
    { fieldName: 'VMVARCODE', fieldLabel: 'Переменная *', fieldIsMandatory: false, fieldType: 'str', fieldArea: 'VENDORINFO', anotherFieldNames: ['VARCODEML'], updateExisted: false, },

    { fieldName: 'VENDORNOTE', fieldLabel: 'Примечание', fieldIsMandatory: false, fieldType: 'str', fieldArea: 'VENDORINFO', updateExisted: true, },
    { fieldName: 'EUPRICEEXPR', fieldLabel: 'Старый расчет выходной цены', fieldIsMandatory: false, fieldType: 'str', fieldArea: 'VENDORINFO', updateExisted: false, },
    { fieldName: 'VPINCOMPRICEXPR', fieldLabel: 'Старый расчет входной цены', fieldIsMandatory: false, fieldType: 'str', fieldArea: 'VENDORINFO', updateExisted: false, },

  ];

  /** Emits `false` on dialog hide to support two-way `[(display)]` binding. */
  @Output() displayChange = new EventEmitter();

  /** Emits the final `Product[]` array after all rows are saved. */
  @Output() importIsDone = new EventEmitter<Product[]>();

  modal: boolean = true;

  blocked: boolean = false;
  loading: boolean = false;
  saving: boolean = false;

  private _getProductQuery: Subscription;
  private _dataSourceQuery: Subscription;
  private _groupInfoQuery: Subscription;

  /** True when the confirm button should be disabled — no products yet or a load is in progress. */
  public get notReady(): boolean { return ((this.products.length == 0) || this.loading); }

  products: Product[] = [];

  vendors: ProdVendors[] = [];

  vendor: ProdVendors = null;

  stateSaveString: string = 'import-product-array';

  viewColumnSet: string[] = [
    'PRODCODE',
//    'PRODARTICUL',
    'PRODNAME',
    'PRODDESCRIPTION',
//    'PRODNOTE',
//    'PRODCUSTCODE',
//    'PRODGROUPNAME',
    'PRODVENDORNAME', 
    'PRODPRICEUAH',
  ];

  textSource: string = '';

  dataSource: any[] = [];
  columns: string[] = [];

  createNewOnly: boolean = true;
  clearBefore: boolean = true;
  overwriteExisted: boolean = true;
  addExisted: boolean = false;

  textType: string = 'tab';   // Either 'csv' so far
  textDelimiter: string = '\t';

  /** True when the given column header is already mapped to a `paramsMap` entry. */
  public columnMet(col: string): boolean { return (this.paramsMap.find(e => (e.importColumnName || '') == col)) ? true : false; }

  /** True when a result row has the `HasWarningsOrErrors` flag set; used to highlight rows in the result table. */
  public hasWarningsOrErrors(row: any): boolean { return row && row['HasWarningsOrErrors']; }

  /** True when every mandatory `paramsMap` field has a mapped Excel column. Guards the import button. */
  public get canInsertData(): boolean {
    let canInsert = true;

    this.paramsMap
      .filter(e => e.fieldIsMandatory)
      .forEach(e => canInsert = canInsert && this.columns.includes(e.importColumnName || ''))
    return canInsert;
  };

  /** True when the pasted text area is non-empty; guards the text-insert button. */
  public get canInsertTextData(): boolean { return (this.textSource || '').trim() != ''; };

  openFileSelector: boolean = true;
  openFieldSelector: boolean = false;
  openImportResult: boolean = false;

  processDateField: boolean = false;

  selectedDateProcess: string = 'Pure Excel (44358)';

  dateProcessType: string[] = [
    this.selectedDateProcess,
    'Pure Date (from JSON)',
    'Default Javascript (MM/DD/YYYY)',
    'More usefull (DD/MM/YYYY)',
    'Even More usefull (DD.MM.YYYY)',
    'Trick 1 (20210123 => 2021-01-23)',
    'Trick 2 (20212307 => 2021-07-23)',
    'Trick 3 (2021-07-23 => 2021-07-23)',
    'Don\'t know, honestly'
  ];

  constructor(
    private prodApi: ProductApiService,
    private cliApi: ClientApiService,
    private restApi: RestApiService,
    private messageService: MessageService,
    private globalsettingsservice: GetglobalsettingsService,
  ) { }

  /**
   * Reacts to `@Input` changes:
   * - `paramsMap` — sets `processDateField` when any entry has `fieldType === 'dat'`.
   * - `display`   — resets `products`, `columns`, `textSource`, mode flags, and
   *                 stepper state when the dialog opens.
   */
  ngOnChanges(changes: SimpleChanges) {

    for (let propName in changes) {
      let change = changes[propName];

      console.log('*** ImportProductInfoDlgComponent ngOnChanges ' + propName + ' (actual): ' + JSON.stringify(change.currentValue));
      //console.log('*** CreateNewDocumentComponent ngOnChanges ' + propName + ' (was):    ' + JSON.stringify(change.previousValue));

      switch (propName) {

        case 'paramsMap':
          this.processDateField = this.paramsMap.find(e => (e.fieldType) == 'dat') ? true : false;
          break;

        case 'display':
          if (change.currentValue) {
            this.products = [];
            this.columns = [];
            this.textSource = '';

            this.clearBefore = true;
            this.overwriteExisted = true;
            this.addExisted = false;

            this.openFileSelector = true;
            this.openFieldSelector = false;
            this.openImportResult = false;
          }
          break;
      }
    }
  }

  /** Unsubscribes all active subscriptions to prevent memory leaks. */
  ngOnDestroy(): void {
    console.log('*** ImportProductInfoDlgComponent in ngOnDestroy')

    if (this._getProductQuery) { this._getProductQuery.unsubscribe(); }

    if (this._dataSourceQuery) { this._dataSourceQuery.unsubscribe(); }

    if (this._groupInfoQuery) { this._groupInfoQuery.unsubscribe(); }
  }

  /** Loads global settings. The import pipeline is driven by `onDataInsert` rather than a long-lived Subject. */
  ngOnInit(): void {
    console.log('*** ImportProductInfoDlgComponent in ngOnInit');

    this.globalSettinsData = this.globalsettingsservice.getGlobalSettings();

    // Default VAT rate for imported products comes from configuration, not a hardcoded 20 (0 is a valid rate).
    const vatField = this.paramsMap.find(e => e.fieldName == 'PRODVAT');
    if (vatField) { vatField.defaultValue = String(this.globalSettinsData.vatValue); }
  }

  /** Called by PrimeNG dialog `(onShow)` — debug hook. */
  onShowDialog() {
    console.log('*** ImportProductInfoDlgComponent onShow: ');
  }

  /** Clears any toast messages and emits `false` via `displayChange` to close the dialog. */
  onHideDialog() {
    console.log('*** ImportProductInfoDlgComponent onHide');
    this.messageService.clear();
    this.displayChange.emit(false);
  }

  /**
   * Extracts column headers from the first row of `dataSource` into `columns`,
   * auto-maps matched columns to `paramsMap` entries (by label, field name, or alias),
   * sets `updateExisted` flags, and advances the stepper to the field-selector step.
   */
  processDataSource() {
    this.columns = [];

    console.log('*** ImportProductInfoDlgComponent reader.sheet_to_json: ' + JSON.stringify(this.dataSource));
    if ((this.dataSource || []).length > 0) {

      environment.production || console.log('*** ImportProductInfoDlgComponent reader.sheet_to_json[0]: ' + JSON.stringify(this.dataSource[0]));
      for (const keys in this.dataSource[0]) {
        environment.production || console.log('*** ImportProductInfoDlgComponent reader.data.keys: ' + keys);

        let column: string = keys.trim();

        this.columns.push(column);

        let a = this.paramsMap.find(e => (e.fieldLabel == column)) ?? this.paramsMap.find(e => ((e.fieldName == column) || (e.anotherFieldNames || []).includes(column)));

        if (a) {
          a.importColumnName = column;
        }
      }

      environment.production || console.table(this.paramsMap);

      this.paramsMap.forEach(e => {
        e.updateExisted = !((e.importColumnName || '') == '');
      });

//      this.dataSource[0]['HasWarningsOrErrors'] = false; // Create status field for each row

      this.openFileSelector = false;
      this.openFieldSelector = true;
    } else {
      console.log('*** ImportProductInfoDlgComponent reader.sheet_to_json: SEEMS EMPTY');
    }
  }

  /**
   * Advances the stepper to the import-result step and builds the `products` array.
   * Iterates `dataSource` rows via `from + concatMap`; for each row looks up PRODCODE
   * via `getProductByCode`, then either updates the existing product or creates a new
   * one from `newProductTemplate`. VENDORINFO fields are posted separately as
   * `ProdVendors` records after the product is saved.
   */
  onDataInsert(event) {
    console.log('*** ImportProductInfoDlgComponent onDataInsert: ' + JSON.stringify(event));

    this.openFieldSelector = false;
    this.openImportResult = true;

    if (this.canInsertData) {
      /*
          { fieldName: 'DOCITEMPRODNAME', fieldLabel: 'Наименование товара', fieldIsMandatory: false, fieldType: 'str' },
          { fieldName: 'DOCITEMPRICE', fieldLabel: 'Цена товара', fieldIsMandatory: true, fieldType: 'num' },
          { fieldName: 'DOCITEMQTY', fieldLabel: 'Количество', fieldIsMandatory: true, fieldType: 'num' },
          { fieldName: 'DOCITEMVPRICE', fieldLabel: 'Цена в валюте', fieldIsMandatory: false, fieldType: 'num' },
          { fieldName: 'DOCITEMTEXT1', fieldLabel: 'Примечание 1', fieldIsMandatory: false, fieldType: 'str' },
          { fieldName: 'DOCITEMTEXT2', fieldLabel: 'Примечание 2', fieldIsMandatory: false, fieldType: 'str' },
          { fieldName: 'U4ETKA', fieldLabel: 'Учетка', fieldIsMandatory: false, fieldType: 'num' },
      */
      let prodCodeColumnName = (this.paramsMap.find(e => (e.fieldName) == 'PRODCODE') || {}).importColumnName || '';

      this.loading = true;
      this.blocked = true;

      if (this.clearBefore) {
        this.products = [];
      }

      this._dataSourceQuery =
        from(this.dataSource)
        .pipe(
            filter(row => { console.log(row[prodCodeColumnName]); return (row[prodCodeColumnName] || '').length > 3; }),

            concatMap(row => this.prodApi.getProductByCode(row[prodCodeColumnName].trim(), true, true).pipe(

              concatMap(prod => {
                environment.production || console.log('*** ImportProductInfoDlgComponent in onDataInsert row: ' + JSON.stringify(row));
                environment.production || console.log('*** ImportProductInfoDlgComponent in onDataInsert getProductByCode: ' + JSON.stringify(prod));

                let product: Product = prod[0];
                row['HasWarningsOrErrors'] = false; // Reset status for each row

                row['Статус'] = 'Found';

                if (prod.length < 1) {
                  row['Статус'] = 'Create new';
                  environment.production || console.log('*** ImportProductInfoDlgComponent in onDataInsert getProductByCode: Create New');

                  product =
                  {
                    ...this.newProductTemplate || {

                      PRODID: -2,
                      PRODVENDORID: 0,
                      PRODCODE: row[prodCodeColumnName].trim(),
                      PRODNAME: '',
                      PRODDESCRIPTION: '',
                      PRODNOTE: '',
                      PRODSALERID: this.globalSettinsData.mainPersonId,
                      PRODARTICUL: '',
                      PRODSTOCK: 0,
                      PRODONHAND: 0,
                      PRODRESERV: 0,
                      PRODGROUP: 0,
                      PRODWEIGHT: 0,
                      PRODPACK: 0,
                      PRODPACKGW: 0,
                      PRODPACKVOL: 0,
                      PRODPRICEVID: 0,
                      PRODCUSTCODE: '',
                      PRODBARCODEMASK: '',
                      PRODWARRANTY: 0,
                      PRODBASEDISC: 0,
                      PRODQTYDISC: 0,
                      PRODDIALDISC: 0,
                      PRODDELTERM: 0,
                      PRODEUPRICEID: 0,
                      PRODKIND: 0,
                      PRODOBSOLETE: 0,
                      PRODWWWPAGE: '',
                      PRODCHECKMETHOD: '',
                      PRODVAT: this.globalSettinsData.vatValue, // configured default; an Excel PRODVAT column overrides it below
                    },

                    ... {
                      PRODID: -2,
                      PRODCODE: row[prodCodeColumnName].trim(),
                      PRODSALERID: this.globalSettinsData.mainPersonId,
                      PRODPRICEVID: 0,

                      changed: true
                    },
                  };
                } else {
                  product.changed = false;
                }

                return of(product);
              }),

//              tap(product => environment.production || console.table(product)),

              // Check if we really have something to insert
              filter(product => (product != null)),
              // Filter only new (checkbox controlled)
              filter(product => (!this.createNewOnly || product.PRODID == -2)),
              // Avoid duplicates (checkbox controlled)
              filter(product => (this.products.find(p => (p.PRODCODE == product.PRODCODE)) == undefined)),

//              tap(product => environment.production || console.table(product)),

              concatMap(product => (product.PRODID > 0 ? this.prodApi.getProdVendors(product.PRODID) : of([])).pipe(
                // A not-yet-saved product (PRODID -2) has no vendor rows to look up —
                // /Product/:id/Vendors 400s on a non-positive id, which previously killed
                // the whole import subscription on the first brand-new row.
                catchError(err => {
                  console.error('*** ImportProductInfoDlgComponent getProdVendors error for "' + product.PRODCODE + '": ', err);
                  row['Статус'] = 'VENDOR lookup failed';
                  row['HasWarningsOrErrors'] = true;
                  return of([]);
                }),
                map(data => {
                row['Статус'] = 'VFound';

//                environment.production || console.table(data);
                /*
                 * This is wrong idea due to asyncronous rasing
                 *
                // this.vendors = data;
                // this.vendor = this.vendors.find(v => (v.VENDORID == product.PRODPRICEVID)) ?? this.vendors[0];
                 *
                 * Next cycle starts before current sycle obtains information from relation queries to database
                 * (starts another thead or something similar)
                 * So, everithing should be done on local copies of data
                 *
                 * a) new product without vendors set
                 * b) existed product with vendors set and default price row
                 * c) existed product with 1+ vendors but price definition row does not set
                 */

                product._PRODVENDORS = data;

                //mark all data as unchanged
                product._PRODVENDORS.forEach(e => e.cloneIndex = 0);

                /*
                 * TODO: create new porodvendor row in case there is not avalable
                 */

                let sd: string = new Date().toISOString().slice(0, 10);

                if (product._PRODVENDORS.length < 1) {

                  product._PRODVENDORS = [

                    (this.newProductTemplate && ((this.newProductTemplate._PRODVENDORS || []).length > 0)) ?

                      {
                        ...this.newProductTemplate._PRODVENDORS.find(e => (this.newProductTemplate.PRODPRICEVID == e.VENDORSITEMID)),
                        VENDORSITEMID: -2,
                        PRODID: product.PRODID,
                        VENDORNOTE: sd + ': Created during import (copy of ' + this.newProductTemplate.PRODCODE + ')',
                        cloneIndex: 1
                      }

                    :
                      {
                        VENDORSITEMID: -2,
                        VENDORID: product.PRODVENDORID,
                        PRODID: product.PRODID,
                        VENDORNOTE: sd + ': Created during import',
                        VENDORPRICE: 0,
                        VPCURRID: this.globalSettinsData.balanceCurrencyId,
                        VPINCOMPRICEXPR: '',
                        EUPRICEEXPR: '',
                        VENDORMARGIN: 1.6,
                        VENROUNDBASE: 1,
                        VPADDVAR: null,
                        VPADDCONST: null,
                        VPMULVAR: null,
                        VPMULCONST: null,
                        cloneIndex: 1
                      }
                  ];

                  product.changed = true;

//                  environment.production || console.table(product);
                }

                if ((!product.PRODPRICEVID || product.PRODPRICEVID == -2) && (product._PRODVENDORS.length > 0)) {

                  // TODO: For newly created product have to be fixed on (first) save

                  product.PRODPRICEVID = product._PRODVENDORS[0].VENDORSITEMID;

                  product.changed = true;
                }

//                environment.production || console.table(product);

                return product;
              }))),

//              tap(product => environment.production || console.table(product)),

              concatMap(product => {

                this.paramsMap
                  .filter(e => (e.importColumnName ?? '') != '')
                  .filter(e => ((row['Статус'] == 'Create new') || e.updateExisted))
                  .forEach(e => {

                    environment.production || console.log('*** ImportProductInfoDlgComponent in onDataInsert paramsMap (' + product.PRODCODE + '): ' + JSON.stringify(e));

                    let name: string = e.importColumnName;
                    let field: string = e.fieldName;

                    switch (field) {
                      case 'RESULT':
                        break;

                      case 'PRODCODE':
                        // Do nothing
                        break;

                      case 'PRODARTICUL':
                      case 'PRODBARCODEMASK':
                      case 'PRODNAME':
                      case 'PRODDESCRIPTION':
                      case 'PRODNOTE':
                      case 'PRODWWWPAGE':
                      case 'PRODCUSTCODE':
                        // Process string fields
                        let v: string = row[name];
                        if (product[field] != v) {
                          product[field] = v;
                          product.changed = true;

                          row['Статус'] = 'PRODUCT updated';
                          environment.production || console.log('*** ImportProductInfoDlgComponent in onDataInsert paramsMap status(' + product.PRODCODE + '): ' + row['Статус']);
                        }
                        break;

                      case 'PRODBASEDISC':
                      case 'PRODQTYDISC':
                      case 'PRODDIALDISC':
                      case 'PRODVAT':
                      case 'PRODWEIGHT':
                      case 'PRODPACK':
                      case 'PRODPACKGW':
                      case 'PRODPACKVOL':
                      case 'PRODWARRANTY':
                      case 'PRODDELTERM':
                        // Process number fields
                        // parseMyFloatEx() returns NaN for a numeric 0 (`if (!txt)`), so a real 0% VAT cell would be
                        // read as blank; pass a numeric VAT cell through as it is.
                        let q: number = (field === 'PRODVAT' && typeof row[name] === 'number') ? row[name] : parseMyFloatEx(row[name]);

                        // A blank/unparseable VAT cell is "missing", not a rate: keep the product's configured default (0 is valid).
                        if (field === 'PRODVAT' && !Number.isFinite(q)) {
                          break;
                        }

                        if (product[field] != q) {
                          product[field] = q;
                          product.changed = true;
                          row['Статус'] = 'PRODUCT updated';
                          environment.production || console.log('*** ImportProductInfoDlgComponent in onDataInsert paramsMap status for \"' + field +'\": ' + row['Статус']);
                        }
                        break;

                      case 'PRODGROUPNAME':
                        this._groupInfoQuery = this.prodApi.searchProdGroups(row[name])
                          .pipe(
                            map(data => {
                              let group: ProductGroup = data[0];
                              if (group) {
                                if (product.PRODGROUP != group.PGROUPID) {
                                  product.PRODGROUP = group.PGROUPID;
                                  product.PRODGROUPNAME = group.PGROUPNAME;
                                  product.changed = true;
                                  row['Статус'] = 'PRODUCT updated';
                                  environment.production || console.log('*** ImportProductInfoDlgComponent in onDataInsert paramsMap status for \"' + field + '\": ' + row['Статус']);
                                }
                              } else {
                                this.messageService.add({
                                  severity: 'warn',
                                  summary: 'Группа \"' + row[name] + '\" не найдена',
                                  life: 100000,
                                  detail: 'Группа \"' + row[name] + '\" не найдена в списке существующих групп'
                                });

                                row['Статус'] = 'PRODUCT group not found';
                                row['HasWarningsOrErrors'] = true;
                                environment.production || console.log('*** ImportProductInfoDlgComponent in onDataInsert paramsMap status for \"' + field + '\": ' + row['Статус']);
                              }
                            })
                          )
                          .subscribe();
                        break;

                      case 'PRODVENDORNAME':
                        this._groupInfoQuery = this.cliApi.getClientSearchByNick(row[name])
                          .pipe(
                            map(data => {
                              environment.production || console.log('*** ImportProductInfoDlgComponent in onDataInsert paramsMap (PRODVENDORNAME): ' + JSON.stringify(data));

                              let vendor: Client = data[0];

                              if (vendor) {
                                if (product.PRODVENDORID != vendor.CLIID) {
                                  product.PRODVENDORID = vendor.CLIID;
                                  product.PRODVENDORNAME = vendor.CLIENTSNIKNAME;
                                  product.changed = true;
                                  row['Статус'] = 'PRODUCT updated';
                                  environment.production || console.log('*** ImportProductInfoDlgComponent in onDataInsert paramsMap status for \"' + field + '\": ' + row['Статус']);
                                }
                              } else {
                                this.messageService.add({
                                  severity: 'warn',
                                  summary: 'Поставщик \"' + row[name] + '\" не найден',
                                  life: 100000,
                                  detail: 'Поставщик \"' + row[name] + '\" не найден в списке существующих поставщиков'
                                });
                                row['Статус'] = 'PRODUCT vendor not found';
                                row['HasWarningsOrErrors'] = true;
                                environment.production || console.log('*** ImportProductInfoDlgComponent in onDataInsert paramsMap status for \"' + field + '\": ' + row['Статус']);
                              }
                            })
                          )
                          .subscribe();
                        break;

                      case 'VENDORNIKNAME':
                        if (product._PRODVENDORS) {
                          let vendor = product._PRODVENDORS.find(v => v.VENDORSITEMID == product.PRODPRICEVID);
                          if (vendor) {
                            this._groupInfoQuery = this.cliApi.getClientSearchByNick(row[name])
                              .pipe(
                                map(data => {
                                  let v = data[0];

                                  if (v) {
                                    if (vendor.VENDORID != v.CLIID) {
                                      vendor.VENDORID = v.CLIID;
                                      vendor.VENDORNIKNAME = v.CLIENTSNIKNAME;
                                      vendor.VENDORNAME = v.CLIENTSNAME;
                                      vendor.cloneIndex = 1;       // Mark row as changed
                                      row['Статус'] = 'VENDOR [' + field + '] updated';
                                      environment.production || console.log('*** ImportProductInfoDlgComponent in onDataInsert paramsMap status for \"' + field + '\": ' + row['Статус']);
                                    }
                                  } else {
                                    this.messageService.add({
                                      severity: 'warn',
                                      summary: 'Поставщик \"' + row[name] + '\" не найден',
                                      life: 100000,
                                      detail: 'Поставщик \"' + row[name] + '\" не найден в списке существующих поставщиков'
                                    });
                                    row['Статус'] = 'VENDOR [' + field + '] not found';
                                    row['HasWarningsOrErrors'] = true;
                                    environment.production || console.log('*** ImportProductInfoDlgComponent in onDataInsert paramsMap status for \"' + field + '\": ' + row['Статус']);
                                  }
                                })
                              )
                              .subscribe();
                          }
                        }
                        break;

                      case 'VENDORNOTE':
                      case 'EUPRICEEXPR':
                      case 'VPINCOMPRICEXPR':
                        if (product._PRODVENDORS) {
                          let vendorIndex = product._PRODVENDORS.findIndex(v => v.VENDORSITEMID == product.PRODPRICEVID);

                          if (vendorIndex > -1) {
                            let q: string = row[name];

                            let vendor = product._PRODVENDORS[vendorIndex];

                            if (vendor[field] != q) {
                              vendor[field] = q;
                              vendor.cloneIndex = 1;       // Mark row as changed
                              row['Статус'] = 'VENDOR [' + field + '] updated';
                              environment.production || console.log('*** ImportProductInfoDlgComponent in onDataInsert paramsMap status for \"' + field + '\": ' + row['Статус']);
                            }
                          }
                        }
                        break;

                      case 'VENDORPRICE':
                      case 'VPMULCONST':
                      case 'VPADDCONST':
                      case 'VENDORMARGIN':
                      case 'VENROUNDBASE':
                        if (product._PRODVENDORS) {
                          let vendorIndex = product._PRODVENDORS.findIndex(v => v.VENDORSITEMID == product.PRODPRICEVID);

                          if (vendorIndex > -1) {

                            let q: number = parseMyFloatEx(row[name]);
                            if ((q || 0) == 0) {
                              this.messageService.add({
                                severity: 'warn',
                                summary: 'Значение \"' + field + '\" не определено или равно 0',
                                life: 100000,
                                detail: JSON.stringify(row[name])
                              });
                              row['Статус'] = 'VENDOR [' + field + '] not found';
                              row['HasWarningsOrErrors'] = true;
                            }
                            environment.production || console.log('*** ImportProductInfoDlgComponent in onDataInsert paramsMap status for field \"' + name + '\": ' + row[name] + ' => ' + q);

                            let vendor = product._PRODVENDORS[vendorIndex];

                            if (vendor[field] != q) {
                              vendor[field] = q;
                              vendor.cloneIndex = 1;       // Mark row as changed
                              row['Статус'] = 'VENDOR [' + field + '] updated';
                              environment.production || console.log('*** ImportProductInfoDlgComponent in onDataInsert paramsMap status for \"' + field + '\": ' + row['Статус']);
                            }
                          } else {
                            this.messageService.add({
                              severity: 'warn',
                              summary: 'Поставщик не найден для \"' + row[prodCodeColumnName] + '\"',
                              life: 100000,
                              detail: 'Поставщик не найден для \"' + row[prodCodeColumnName] + '\"'
                            });
                            row['Статус'] = 'VENDOR [' + field + '] not found';
                            row['HasWarningsOrErrors'] = true;
                            environment.production || console.log('*** ImportProductInfoDlgComponent in onDataInsert paramsMap status for \"' + field + '\": ' + row['Статус']);
                          }
                        } else {
                          this.messageService.add({
                            severity: 'error',
                            summary: 'Поставщик не найден для \"' + row[prodCodeColumnName] + '\"',
                            life: 100000,
                            detail: 'Поставщик не найден для \"' + row[prodCodeColumnName] + '\"'
                          });
                          row['Статус'] = 'VENDOR [' + field + '] not found';
                          row['HasWarningsOrErrors'] = true;
                          environment.production || console.log('*** ImportProductInfoDlgComponent in onDataInsert paramsMap status for \"' + field + '\": ' + row['Статус']);
                        }
                        break;

                      case 'CURCODE':
                        if (product._PRODVENDORS) {
                          let vendor = product._PRODVENDORS.find(v => v.VENDORSITEMID == product.PRODPRICEVID);
                          if (vendor) {
                            this._groupInfoQuery = this.restApi.getCurrency()
                              .pipe(
                                map(data => {
                                  let c = data;

                                  let v = c.find(e => (e.CURCODE == row[name]));

                                  if (v) {
                                    if (vendor.VPCURRID != v.CURID) {
                                      vendor.VPCURRID = v.CURID;
                                      vendor.CURCODE = v.CURCODE;
                                      vendor.CURNAME = v.CURNAME;

                                      vendor.cloneIndex = 1;       // Mark row as changed
                                      row['Статус'] = 'VENDOR CURR updated';
                                      environment.production || console.log('*** ImportProductInfoDlgComponent in onDataInsert paramsMap status for \"' + field + '\": ' + row['Статус']);
                                    }
                                  } else {
                                    this.messageService.add({
                                      severity: 'warn',
                                      summary: 'Валюта \"' + row[name] + '\" не найдена',
                                      life: 100000,
                                      detail: 'Валюта \"' + row[name] + '\" не найдена в списке существующих валют'
                                    });
                                    row['Статус'] = 'VENDOR [' + field + '] not found';
                                    row['HasWarningsOrErrors'] = true;
                                    environment.production || console.log('*** ImportProductInfoDlgComponent in onDataInsert paramsMap status for \"' + field + '\": ' + row['Статус']);
                                  }
                                })
                              )
                              .subscribe();
                          } else {
                            this.messageService.add({
                              severity: 'warn',
                              summary: 'Поставщик не найден для \"' + row[prodCodeColumnName] + '\"',
                              life: 100000,
                              detail: 'Поставщик не найден для \"' + row[prodCodeColumnName] + '\"'
                            });
                            row['Статус'] = 'VENDOR [' + field + '] not found';
                            row['HasWarningsOrErrors'] = true;
                            environment.production || console.log('*** ImportProductInfoDlgComponent in onDataInsert paramsMap status for \"' + field + '\": ' + row['Статус']);
                          }
                        } else {
                          this.messageService.add({
                            severity: 'error',
                            summary: 'Поставщик не найден для \"' + row[prodCodeColumnName] + '\"',
                            life: 100000,
                            detail: 'Поставщик не найден для \"' + row[prodCodeColumnName] + '\"'
                          });
                          row['Статус'] = 'VENDOR [' + field + '] not found';
                          row['HasWarningsOrErrors'] = true;
                          environment.production || console.log('*** ImportProductInfoDlgComponent in onDataInsert paramsMap status for \"' + field + '\": ' + row['Статус']);
                        }
                        break;

                      case 'VAVARVALUE':
                        if (row[name] != '') {
                          if (product._PRODVENDORS) {
                            let vendorIndex = product._PRODVENDORS.findIndex(v => v.VENDORSITEMID == product.PRODPRICEVID);
                            if (vendorIndex > -1) {
                              let vendor = product._PRODVENDORS[vendorIndex];

                              // VPADDVAR null/undefined/0 = "no additive variable assigned" (VARID 0 is the
                              // shared default row, value 0, read by tens of thousands of vendor formulas --
                              // it must never be edited from an import). Skip explicitly instead of asking
                              // the API: GET /Variables/null resolves to VARID 0, and only the falsy 0 in the
                              // `vra.VARID &&` check below used to keep this from touching it.
                              if (!vendor.VPADDVAR) {
                                environment.production || console.log('*** ImportProductInfoDlgComponent in onDataInsert paramsMap status for \"' + field + '\": vendor has no additive variable (VPADDVAR ' + vendor.VPADDVAR + '), value not applied');
                                break;
                              }

                              this._groupInfoQuery = this.prodApi.getPriceVariableById(vendor.VPADDVAR)
                                .pipe(
                                  concatMap(data => {
                                    let vra: PriceVariable = data[0];
                                    let q: number = parseMyFloatEx(row[name]);

                                    if (vra.VARID && (parseFloat(vra.VARVALUE) != q)) {
                                      vra.VARVALUE = q.toString();

                                      // Status is set from the PUT's own result below, not
                                      // optimistically here -- PUT /Variables/:id didn't exist
                                      // on the backend until 2026-08-23 (Prompt 13-D), so every
                                      // real import silently 404'd while this row still reported
                                      // "updated". Route now exists, but a future failure (bad
                                      // VARID, DB constraint) should still be reported honestly.
                                      return this.prodApi.putPriceVariableById(vra.VARID, vra).pipe(
                                        tap(() => {
                                          vendor.cloneIndex = 1;       // Mark row as changed
                                          row['Статус'] = 'VENDOR VPADDVAR value updated';
                                          environment.production || console.log('*** ImportProductInfoDlgComponent in onDataInsert paramsMap status for \"' + field + '\": ' + row['Статус']);
                                        }),
                                        catchError(() => {
                                          this.messageService.add({
                                            severity: 'error',
                                            summary: 'Не удалось обновить значение переменной для \"' + row[prodCodeColumnName] + '\"',
                                            life: 100000,
                                            detail: 'Не удалось обновить значение переменной для \"' + row[prodCodeColumnName] + '\"'
                                          });
                                          row['Статус'] = 'VENDOR VPADDVAR update FAILED';
                                          row['HasWarningsOrErrors'] = true;
                                          return of(null);
                                        })
                                      );
                                    }
                                    return of(null);
                                  })
                                )
                                .subscribe();
                            } else {
                              this.messageService.add({
                                severity: 'warn',
                                summary: 'Поставщик не найден для \"' + row[prodCodeColumnName] + '\"',
                                life: 100000,
                                detail: 'Поставщик не найден для \"' + row[prodCodeColumnName] + '\"'
                              });
                              row['Статус'] = 'VENDOR [' + field + '] not found';
                              row['HasWarningsOrErrors'] = true;
                              environment.production || console.log('*** ImportProductInfoDlgComponent in onDataInsert paramsMap status for \"' + field + '\": ' + row['Статус']);
                            }
                          } else {
                            this.messageService.add({
                              severity: 'error',
                              summary: 'Поставщик не найден для \"' + row[prodCodeColumnName] + '\"',
                              life: 100000,
                              detail: 'Поставщик не найден для \"' + row[prodCodeColumnName] + '\"'
                            });
                            row['Статус'] = 'VENDOR [' + field + '] not found';
                            row['HasWarningsOrErrors'] = true;
                            environment.production || console.log('*** ImportProductInfoDlgComponent in onDataInsert paramsMap status for \"' + field + '\": ' + row['Статус']);
                          }
                        }
                        break;

                      case 'VAVARCODE':
                        if (row[name] != '') {
                          if (product._PRODVENDORS) {
                            let vendorIndex = product._PRODVENDORS.findIndex(v => v.VENDORSITEMID == product.PRODPRICEVID);
                            if (vendorIndex > -1) {
                              let vendor = product._PRODVENDORS[vendorIndex];
                              this._groupInfoQuery = this.prodApi.getPriceVariables()
                                .pipe(
                                  map(data => {
                                    environment.production || console.log('*** ImportProductInfoDlgComponent in onDataInsert paramsMap looking for variable name: ' + row[name]);
                                    let vra = data.find(v => v.VARCODE == row[name]);

                                    if (vra) {
                                      if (vendor.VPADDVAR != vra.VARID) {

                                        vendor.VPADDVAR = vra.VARID;
                                        vendor.cloneIndex = 1;       // Mark row as changed
                                        row['Статус'] = 'VENDOR VPADDVAR updated';
                                        environment.production || console.log('*** ImportProductInfoDlgComponent in onDataInsert paramsMap status for \"' + field + '\": ' + row['Статус']);
                                      }
                                    } else {
                                      this.messageService.add({
                                        severity: 'warn',
                                        summary: 'Переменная \"' + row[name] + '\" не найдена',
                                        life: 100000,
                                        detail: 'Переменная \"' + row[name] + '\" не найдена в списке существующих переменных'
                                      });
                                      row['Статус'] = 'VENDOR [' + field + '] not found';
                                      row['HasWarningsOrErrors'] = true;
                                      environment.production || console.log('*** ImportProductInfoDlgComponent in onDataInsert paramsMap status for \"' + field + '\": ' + row['Статус']);
                                    }
                                  })
                                )
                                .subscribe();
                            } else {
                              this.messageService.add({
                                severity: 'warn',
                                summary: 'Поставщик не найден для \"' + row[prodCodeColumnName] + '\"',
                                life: 100000,
                                detail: 'Поставщик не найден для \"' + row[prodCodeColumnName] + '\"'
                              });
                              row['Статус'] = 'VENDOR [' + field + '] not found';
                              row['HasWarningsOrErrors'] = true;
                              environment.production || console.log('*** ImportProductInfoDlgComponent in onDataInsert paramsMap status for \"' + field + '\": ' + row['Статус']);
                            }
                          } else {
                            this.messageService.add({
                              severity: 'error',
                              summary: 'Поставщик не найден для \"' + row[prodCodeColumnName] + '\"',
                              life: 100000,
                              detail: 'Поставщик не найден для \"' + row[prodCodeColumnName] + '\"'
                            });
                            row['Статус'] = 'VENDOR [' + field + '] not found';
                            row['HasWarningsOrErrors'] = true;
                            environment.production || console.log('*** ImportProductInfoDlgComponent in onDataInsert paramsMap status for \"' + field + '\": ' + row['Статус']);
                          }
                        }
                        break;

                      case 'VMVARVALUE':
                        if (row[name] != '') {
                          if (product._PRODVENDORS) {
                            let vendorIndex = product._PRODVENDORS.findIndex(v => v.VENDORSITEMID == product.PRODPRICEVID);
                            if (vendorIndex > -1) {
                              let vendor = product._PRODVENDORS[vendorIndex];
                              environment.production || console.log('*** ImportProductInfoDlgComponent in onDataInsert paramsMap status for \"' + field + '\": ' + row['Статус']);
                            }
                          }
                        }
                        break;

                      case 'VMVARCODE':
                        if (row[name] != '') {
                          if (product._PRODVENDORS) {
                            let vendorIndex = product._PRODVENDORS.findIndex(v => v.VENDORSITEMID == product.PRODPRICEVID);
                            if (vendorIndex > -1) {
                              let vendor = product._PRODVENDORS[vendorIndex];
                              this._groupInfoQuery = this.prodApi.getPriceVariables()
                                .pipe(
                                  map(data => {
                                    let vra = data.find(v => v.VARCODE == row[name]);

                                    if (vra) {
                                      if (vendor.VPMULVAR != vra.VARID) {

                                        vendor.VPMULVAR = vra.VARID;
                                        vendor.cloneIndex = 1;       // Mark row as changed
                                        row['Статус'] = 'VENDOR VPMULVAR updated';
                                        environment.production || console.log('*** ImportProductInfoDlgComponent in onDataInsert paramsMap status for \"' + field + '\": ' + row['Статус']);
                                      }
                                    } else {
                                      this.messageService.add({
                                        severity: 'warn',
                                        summary: 'Переменная \"' + row[name] + '\" не найдена',
                                        life: 100000,
                                        detail: 'Переменная \"' + row[name] + '\" не найдена в списке существующих переменных'
                                      });
                                      row['Статус'] = 'VENDOR [' + field + '] not found';
                                      row['HasWarningsOrErrors'] = true;
                                      environment.production || console.log('*** ImportProductInfoDlgComponent in onDataInsert paramsMap status for \"' + field + '\": ' + row['Статус']);
                                    }
                                  })
                                )
                                .subscribe();
                            } else {
                              this.messageService.add({
                                severity: 'warn',
                                summary: 'Поставщик не найден для \"' + row[prodCodeColumnName] + '\"',
                                life: 100000,
                                detail: 'Поставщик не найден для \"' + row[prodCodeColumnName] + '\"'
                              });
                              row['Статус'] = 'VENDOR [' + field + '] not found';
                              row['HasWarningsOrErrors'] = true;
                              environment.production || console.log('*** ImportProductInfoDlgComponent in onDataInsert paramsMap status for \"' + field + '\": ' + row['Статус']);
                            }
                          } else {
                            this.messageService.add({
                              severity: 'error',
                              summary: 'Поставщик не найден для \"' + row[prodCodeColumnName] + '\"',
                              life: 100000,
                              detail: 'Поставщик не найден для \"' + row[prodCodeColumnName] + '\"'
                            });
                            row['Статус'] = 'VENDOR [' + field + '] not found';
                            row['HasWarningsOrErrors'] = true;
                            environment.production || console.log('*** ImportProductInfoDlgComponent in onDataInsert paramsMap status for \"' + field + '\": ' + row['Статус']);
                          }
                        }
                        break;

                      default:
                        this.messageService.add({
                          severity: 'warn',
                          summary: 'Поле \"' + field + '\" не найдено',
                          life: 100000,
                          detail: 'Поле \"' + field + '\" не найдено в списке доступніх для импорта полей'
                        });
                        row['HasWarningsOrErrors'] = true;
                        console.log('*** ImportProductInfoDlgComponent in onDataInsert paramsMap unmapped field found: ' + field);
                        break;
                    }
                  });

                if (this.overwriteExisted) {

                  let test = this.products.findIndex(p => (p.PRODCODE == product.PRODCODE));
                  if (test > -1) {
                    this.products.splice(test, 1);
                  }
                }

                environment.production || console.log('*** ImportProductInfoDlgComponent in onDataInsert paramsMap (PUSH): ' + JSON.stringify(product));

                if (product.changed || product._PRODVENDORS.find(e => (e.cloneIndex != 0))) {
                  this.products.push(product);
                  row['Статус'] = 'Added';
                }
                return of(product);
              }),

//              tap(product => environment.production || console.table(product)),
            )),

            finalize(() => {
              this.loading = false;
              this.blocked = false;

              this.openFieldSelector = this.dataSource.some(row => row['HasWarningsOrErrors'] === true);
              this.openImportResult = !this.openFieldSelector;

              this.products = [...this.products];
              environment.production || console.log('*** ImportProductInfoDlgComponent in onDataInsert finalize : ' + JSON.stringify(this.products));
            })
          )
          .subscribe();
    }
  }

  /** Upload progress event handler — debug hook. */
  onProgress(event) {
    console.log('*** ImportProductInfoDlgComponent onProgress: ' + JSON.stringify(event));

  }

  /** Emits `importIsDone` with the current `products` array and closes the dialog. */
  onImportContent(event) {
    console.log('*** ImportProductInfoDlgComponent onImportContent');

    environment.production || console.table(this.products);
    this.loading = true;
    this.blocked = true;

    this._dataSourceQuery =
      from(this.products)
      .pipe(
//        tap(prod => environment.production || console.table(prod)),

        filter(prod => (prod != null)),
//        tap(prod => environment.production || console.table(prod._PRODVENDORS)),
        concatMap(p => iif(() => (p.PRODID == -2),

// Save new
          of(p).pipe(
            filter(prod => (prod.PRODID == -2)),

            // Here we need a lot of work to update cross linked tables in right order
            //
            // 1) Save new product and get new prodId
            // 2) Update prodVendors row PRODID by new prodId
            // 3) Save prodVendors row and get new VENDORSITEMID
            // 4) Update Product PRODPRICEVID by newly created VENDORSITEMID and save Product again
            //
            concatMap(prod => this.prodApi.postNewProductInfo(prod).pipe(map(prodId => { prod.PRODID = prodId.PRODID; return prod; } ))),
            concatMap(prod => from(p._PRODVENDORS).pipe(
              concatMap(v => {
                v.PRODID = prod.PRODID;
                environment.production || console.table(v);
                return this.prodApi.postProdVendorById(v.PRODID, v).pipe(map(vId => { prod.PRODPRICEVID = vId.VENDORSITEMID; return prod; }));
              }),
              concatMap(prod => this.prodApi.putProductMainInfo(prod))
            ))
          ),

// Update existing product
//
          of(p).pipe(
            tap(p => environment.production || console.table(p)),
            filter(prod => (prod.PRODID != -2)),

            concatMap(prod => from(prod._PRODVENDORS).pipe(
              tap(v => environment.production || console.table(v)),

              filter(v => (v != null)),

// There is not case to save existing product with newly created prodvendor row (whatever it arising)
//
              concatMap(v => iif(() => (v.VENDORSITEMID == -2),
                of(v).pipe(
                  filter(v => (v.VENDORSITEMID == -2)),
                  concatMap(v => this.prodApi.postProdVendorById(v.PRODID, v).pipe(map(vId => {
                    v.VENDORSITEMID = vId.VENDORSITEMID;
                    prod.PRODPRICEVID = vId.VENDORSITEMID;
                    prod.changed = true;

                    environment.production || console.table(v);

                    return prod;
                  }))),
                ),
                of(v).pipe(
                  filter(v => (v.VENDORSITEMID != -2)),

// cloneIndex above zero tells us that this row was changed

                  filter(v => (v.cloneIndex > 0)),
                  concatMap(v => this.prodApi.putProdVendorById(prod.PRODID, v.VENDORSITEMID, v).pipe(map(r => {
                    v.cloneIndex = 0;

                    environment.production || console.table(v);

                    return prod;
                  })))
                )
              )),

              concatMap(prod => of(prod)),

              finalize(() => {
                environment.production || console.table(prod);

                if (prod.changed) {
                  environment.production || console.log('*** ImportProductInfoDlgComponent onImportContent: here we have to save changed product ***************  ' + JSON.stringify(prod));

                  this.prodApi.putProductMainInfo(prod)
                    .subscribe(
//                      result => environment.production || console.log('*** ImportProductInfoDlgComponent onImportContent: putProductMainInfo.subscribe (result)***************  ' + JSON.stringify(result)),
                      err => environment.production || console.log('*** ImportProductInfoDlgComponent onImportContent: putProductMainInfo.subscribe (error)***************  ' + JSON.stringify(err)));
                }
              })
            )),

          //  tap(p => environment.production || console.table(p)),
          //  filter(prod => prod.changed),
          //  tap(p => environment.production || console.table(p)),
          //  concatMap(prod => this.prodApi.putProductMainInfo(prod))
          ))),

          finalize(() => {
            environment.production || console.table(this.products.filter(prod => prod.changed));
            this.loading = false;
            this.blocked = false;
            this.importIsDone.emit(this.products.filter(prod => prod.changed));
            this.display = false;
          })
        ).subscribe();
  }

  /** Tab-change event handler — debug hook. */
  onTabChange(event) {
    console.log('*** ImportProductInfoDlgComponent onProgress: ' + JSON.stringify(event));
  }

  /**
   * Parses `textSource` as tab/CSV text using `parseTextBuffer` with the current
   * `textDelimiter`, then calls `processDataSource` to extract column headers.
   */
  onInsertText(event) {
    console.log('*** ImportProductInfoDlgComponent onInsertText: ');

    this.dataSource = parseTextBuffer(this.textSource, this.textDelimiter);

    //    this.dataSource = txtToJSON({ data: this.textSource });
    environment.production || console.log('*** ImportProductInfoDlgComponent reader.onload: ' + JSON.stringify(this.dataSource))
    environment.production || console.table(this.dataSource);
    this.processDataSource();
  }

  /**
   * Handles the native `<input type="file">` change event.
   * Reads the first sheet of the uploaded `.xlsx` file via `XLSX.utils.sheet_to_json`
   * and calls `processDataSource` to extract column headers.
   */
  onFileChange(event) {
    console.log('*** ImportProductInfoDlgComponent onBasicUpload: ');

    const target: DataTransfer = <DataTransfer>(event.target);

    this.dataSource = [];

    if (target.files.length !== 1) throw new Error('Cannot use multiple files');

    const reader: FileReader = new FileReader();
    reader.onload = (e: any) => {
      /* read workbook */
      const bstr: string = e.target.result;
      const wb: XLSX.WorkBook = XLSX.read(bstr, { type: 'binary' });

      /* grab first sheet */
      const wsname: string = wb.SheetNames[0];
      const ws: XLSX.WorkSheet = wb.Sheets[wsname];

      environment.production || console.log('*** ImportProductInfoDlgComponent reader.onload: ' + wsname)

      this.dataSource = [...XLSX.utils.sheet_to_json(ws)];

      this.processDataSource();

      /* save data */
      // this.data = <AOA>(XLSX.utils.sheet_to_json(ws, { header: 1 }));
      // console.log(this.data);
    };
    reader.readAsBinaryString(target.files[0]);
  }

  /** Handles the PrimeNG `FileUpload` upload event (alternative upload path). */
  onBasicUpload(event) {
    console.log('*** ImportProductInfoDlgComponent onBasicUpload: ' + JSON.stringify(event));

    const target: DataTransfer = <DataTransfer>(event.files);

    if (target.files.length !== 1) throw new Error('Cannot use multiple files');
    const reader: FileReader = new FileReader();
    reader.onload = (e: any) => {
      /* read workbook */
      const bstr: string = e.target.result;
      const wb: XLSX.WorkBook = XLSX.read(bstr, { type: 'binary' });

      /* grab first sheet */
      const wsname: string = wb.SheetNames[0];
      const ws: XLSX.WorkSheet = wb.Sheets[wsname];

      console.log('*** ImportProductInfoDlgComponent reader.onload: ' + wsname)

      /* save data */
      // this.data = <AOA>(XLSX.utils.sheet_to_json(ws, { header: 1 }));
      // console.log(this.data);
    };
    reader.readAsBinaryString(target.files[0]);
  }

  /** Handles text-type radio button selection; updates `textDelimiter` and resets parsed state. */
  onTextTypeClick(event) {
    console.log('*** ImportProductInfoDlgComponent onTextTypeClick: ' + JSON.stringify(event));

    switch (this.textType) {
      case 'tab': this.textDelimiter = '\t'; break;
      case 'csv': this.textDelimiter = ';'; break;
      default: this.textDelimiter = '\t'; break;
    }
    this.columns = [];
    this.dataSource = [];
  }

  /** Clears all `importColumnName` mappings in `paramsMap` and resets `columns`. */
  onCleanAllClick(event) {
    console.log('*** ImportProductInfoDlgComponent onCleanAllClick: ' + JSON.stringify(event));

    this.paramsMap
      .forEach(e => {
        e.importColumnName = "";
        e.updateExisted = false;
      });
  }

  /** Cancels the import and closes the dialog. */
  onCancelAllClick(event) {
    console.log('*** ImportProductInfoDlgComponent onCancelAllClick: ' + JSON.stringify(event));

    this.paramsMap
      .forEach(e => {
//        e.importColumnName = "";
        e.updateExisted = false;
      });
  }

  /** Clears the `importColumnName` mapping for a single `paramsMap` field entry. */
  onCleanFieldSelection(event, field) {
    console.log('*** ImportProductInfoDlgComponent onCleanFieldSelection: ' + JSON.stringify(event) + JSON.stringify(field));

    field.updateExisted = false;
  }

  /** Sets `importColumnName` on a `paramsMap` entry from the selected dropdown value. */
  onFieldSelectionClick(event, field) {
    console.log('*** ImportProductInfoDlgComponent onFieldSelectionClick: ' + JSON.stringify(event) + JSON.stringify(field));

    field.updateExisted = true;
  }
}
